import useMetadataStore from "@/stores/metaDataStore";
import { CheckCircle, UserRound } from "lucide-react";

// UserMarketingProfile type definition
interface UserMarketingProfile {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  emailMarketingOptIn?: boolean;
  smsMarketingOptIn?: boolean;
}

interface UMPConfirmationProps {
  onConfirm: () => void;
  onReject: () => void;
}

/**
 * User Marketing Profile confirmation screen
 * Shows a personalized welcome when we have user data and asks for confirmation
 */
export const UMPConfirmation = ({
  onConfirm,
  onReject,
}: UMPConfirmationProps) => {
  const { offerMetadata } = useMetadataStore();
  const userMarketingProfile = offerMetadata?.userMarketingProfile;
  const potentialUser = offerMetadata?.user;
  const backgroundImage =
    offerMetadata?.hotel?.guestBookingHeroImageSummerBlobFilePath || "";

  // Check if firstName is invalid, "@user", or just "user"
  const isInvalidUser =
    !userMarketingProfile?.firstName ||
    userMarketingProfile.firstName === "@user" ||
    userMarketingProfile.firstName.toLowerCase() === "user" ||
    potentialUser?.firstName?.toLowerCase() === "user" ||
    potentialUser?.firstName?.toLowerCase() === "@user" ||
    potentialUser?.firstName?.toLowerCase() === "";

  return (
    <div
      className="w-full h-full flex flex-col justify-center items-center p-8 relative overflow-hidden"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Content card with enhanced shadow */}
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-auto text-center z-10 relative border border-white/20 backdrop-filter backdrop-blur-[2px]">
        <div className="flex justify-center mb-4">
          <div className="bg-gray-100 rounded-full p-4">
            <UserRound size={40} className="text-gray-700" />
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-2 text-gray-800">
          Welcome{!isInvalidUser ? `, ${userMarketingProfile.firstName}` : ""}!
        </h2>

        {userMarketingProfile?.email && (
          <p className="text-sm text-gray-500 mb-2">
            {userMarketingProfile.email}
          </p>
        )}

        <p className="text-gray-600 mb-8">
          We have a special offer prepared just for you.
        </p>

        <div className="flex flex-col gap-3">
          {isInvalidUser ? (
            <button
              onClick={onConfirm}
              className="flex items-center justify-center gap-2 bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              <span>Proceed</span>
            </button>
          ) : (
            <>
              <button
                onClick={onConfirm}
                className="flex items-center justify-center gap-2 bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                <CheckCircle size={18} />
                <span>Yes, that's me</span>
              </button>

              <button
                onClick={onReject}
                className="text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                No, that's not me
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UMPConfirmation;
